import { AvatarGroup } from '#app/components/Avatars/AvatarGroup/AvatarGroup.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { type MinimizedCollaboratorsListData } from './types.js';
import { AddIcon, ChevronDownIcon } from '@contra/icons/icons';
import { styled, useTheme } from 'styled-components';

const ICON_SIZE = 16;

const DesktopMinimizedCollaboratorListContainer = styled.div`
  min-width: 246px;

  & > * + * {
    margin-top: 8px;
  }
`;

const MobileMinimizedCollaboratorListContainer = styled.div`
  display: flex;
  align-items: center;

  > * {
    margin-left: 12px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TextButton = styled.button`
  display: flex;
  align-items: center;

  &:hover,
  &:focus {
    opacity: 0.6;
  }

  > svg {
    margin-left: 2px;
  }

  transition: opacity 0.2s ease-in-out;
`;

type MinimizedCollaboratorsListProps = {
  readonly collaborators: MinimizedCollaboratorsListData;
  readonly onSeeMore: () => void;
};

export const MinimizedCollaboratorsList = ({
  collaborators,
  onSeeMore,
}: MinimizedCollaboratorsListProps) => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  if (!collaborators.length) return null;

  if (isMobile) {
    return (
      <MobileMinimizedCollaboratorListContainer>
        <AddIcon
          color={theme.colorsV2.gray50}
          size={16}
          strokeWidth={2}
        />
        <AvatarGroup
          data={collaborators}
          maxCount={4}
          size="sm"
        />
        <TextButton onClick={onSeeMore}>
          <ChevronDownIcon size={ICON_SIZE} />
        </TextButton>
      </MobileMinimizedCollaboratorListContainer>
    );
  } else {
    return (
      <DesktopMinimizedCollaboratorListContainer>
        <HeaderRow>
          <Text
            color="gray60"
            textStyle="bodySmall"
          >
            In Collaboration With
          </Text>
          <TextButton onClick={onSeeMore}>
            <Text textStyle="subtitleSmall">See More</Text>
            <ChevronDownIcon size={ICON_SIZE} />
          </TextButton>
        </HeaderRow>

        <AvatarGroup
          data={collaborators}
          size="sm"
        />
      </DesktopMinimizedCollaboratorListContainer>
    );
  }
};
